import Lenis from 'lenis'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { preloadAssets } from './utils.js'

gsap.registerPlugin(ScrollTrigger)

/**
 * Class to handle the sticky grid scroll animation
 */
class StickyGridScroll {
    constructor() {
        this.getElements()
        if (!this.block) return

        this.initStates()
        this.setupTimeline()
    }

    /**
     * Select and store DOM elements
     */
    getElements() {
        this.block = document.querySelector('.block--main')
        if (this.block) {
            this.wrapper = this.block.querySelector('.block__wrapper')
            this.content = this.block.querySelector('.content')
            this.title = this.block.querySelector('.content__title')
            this.description = this.block.querySelector('.content__description')
            this.gallery = this.block.querySelector('.gallery')
            this.grid = this.block.querySelector('.gallery__grid')
            this.items = this.block.querySelectorAll('.gallery__item')

            // Group items by columns (3 columns)
            this.columns = [[], [], []]
            this.items.forEach((item, index) => {
                this.columns[index % 3].push(item)
            })
        }
    }

    /**
     * Initialize initial states of elements
     */
    initStates() {
        // Preparation for text appearance
        gsap.set(this.description, { opacity: 0, pointerEvents: 'none' })

        // Ensure absolute centering of gallery
        gsap.set(this.gallery, { xPercent: -50, yPercent: -50, x: 0, y: 0 })

        // Title should be centered initially.
        // Since .content is 100vh with flex center, yPercent: 0 is already centered.
        // We will only animate its movement later.
        gsap.set(this.title, { yPercent: 0 })
    }

    /**
     * Setup the main scroll-driven timeline
     */
    setupTimeline() {
        // Universal matchMedia to handle portrait/landscape/desktop
        const mm = gsap.matchMedia()

        mm.add(
            {
                isDesktop: '(min-width: 768px)',
                isMobile: '(max-width: 767px)',
                isLandscape: '(orientation: landscape) and (max-height: 500px)',
            },
            (context) => {
                const { isDesktop, isLandscape } = context.conditions

                // Ensure layout is stable - wait for final repaint
                const targetScale = 1.9
                const galleryScale = 0.25

                // 1. Calculations
                const wh = window.innerHeight
                const gridH = this.grid.offsetHeight || 1000 // Fallback for 0 height on load

                // Multiplier for centering depends on screen aspect ratio
                const aspect = window.innerWidth / window.innerHeight
                // We use a larger multiplier for panoramic to ensure upward shift compensates the spread
                const multiplier = isDesktop
                    ? aspect > 1.8
                        ? 1.6
                        : 1.3
                    : isLandscape
                      ? 1.9
                      : 1.35
                const finalHeight =
                    gridH * targetScale * galleryScale * multiplier
                const yOffset = Math.max(0, (finalHeight - wh) / 2)

                // Sync distances logic
                const dy = wh / galleryScale / 2 + gridH / 2 + 200

                // 2. TIMELINE Setup
                //const debugEl = document.getElementById('debug-progress')
                const tl = gsap.timeline({
                    scrollTrigger: {
                        trigger: this.block,
                        start: 'top 25%', // Standardized for all viewports
                        end: 'bottom bottom',
                        scrub: true,
                        /*onUpdate: (self) => {
                            if (debugEl) {
                                debugEl.innerText = `Progress: ${(self.progress * 100).toFixed(0)}%`
                            }
                        },*/
                        onRefresh: () => ScrollTrigger.update(), // Force sync
                    },
                })

                // Ensure centering is initialized inside the matchMedia block
                gsap.set(this.gallery, { xPercent: -50, yPercent: -50, y: 0 })
                gsap.set(this.content, { y: 0 })
                gsap.set(this.title, { yPercent: 0 })

                // --- 0% to 40%: Symmetric Reveal ---
                tl.addLabel('reveal', 0)

                // Side columns descend - Separate twins on same label to be strictly parallel
                tl.from(
                    this.columns[0],
                    {
                        y: -dy,
                        duration: 0.35, // Explicitly ends before message
                        stagger: { each: 0.05, from: 'end' },
                        ease: 'none',
                    },
                    'reveal',
                )

                tl.from(
                    this.columns[2],
                    {
                        y: -dy,
                        duration: 0.35, // Explicitly ends before message
                        stagger: { each: 0.05, from: 'end' },
                        ease: 'none',
                    },
                    'reveal',
                )

                // Central column ascends
                tl.from(
                    this.columns[1],
                    {
                        y: dy,
                        duration: 0.35, // Explicitly ends before message
                        stagger: { each: 0.05, from: 'start' },
                        ease: 'none',
                    },
                    'reveal',
                )

                // --- 25% to 50%: Progressive Zoom ---
                tl.to(
                    this.grid,
                    {
                        scale: targetScale,
                        duration: 0.25,
                        ease: 'power1.inOut',
                    },
                    0.25,
                )

                // --- 50% to 80%: Message Reveal & Spread ---
                tl.addLabel('message', 0.5)

                // Text behavior: description appears at 50%
                tl.to(
                    this.description,
                    {
                        opacity: 1,
                        pointerEvents: 'all',
                        duration: 0.1, // Quick reveal to be readable by 60%
                        ease: 'power2.out',
                    },
                    'message',
                )

                // Lateral images move out to reveal text
                tl.to(
                    this.columns[0],
                    { xPercent: -40, duration: 0.3 },
                    'message',
                )
                tl.to(
                    this.columns[2],
                    { xPercent: 40, duration: 0.3 },
                    'message',
                )

                // Central images spread vertically
                const vSpread = isLandscape ? 30 : isDesktop ? 35 : 80

                tl.to(
                    this.columns[1],
                    {
                        yPercent: (i, target, targets) =>
                            i < targets.length / 2 ? -vSpread : vSpread,
                        duration: 0.1, // Faster spread to allow longer dwell
                    },
                    'message',
                )

                // --- 60% to 80%: STATIC DWELL ---
                // Perfectly immobile for reading the focal text.

                // --- 80% to 100%: LINEAR SCROLL-OUT ---
                // Move the gallery so its bottom aligns with the bottom of the viewport
                // instead of moving it a full viewport height upward.
                tl.to(
                    [this.gallery, this.content],
                    {
                        y: -yOffset,
                        duration: 0.2, // 80% to 100%
                        ease: 'none',
                    },
                    0.8,
                )

                // --- 100%: Natural Release (Unpin) ---
            },
        )

        // Parallax Effect for the Section Wrapper (Before Sticky holds it)
        gsap.from(this.wrapper, {
            yPercent: -100,
            ease: 'none',
            scrollTrigger: {
                trigger: this.block,
                start: 'top bottom',
                end: 'top top',
                scrub: true,
            },
        })
    }
}

/**
 * Arrow Animation (Preserved)
 */
function initArrowAnimation() {
    const arrowContainer = document.querySelector('.intro__arrow-inner')
    if (!arrowContainer) return
    const p1 = arrowContainer.querySelector('.arrow-path-1')
    const p2 = arrowContainer.querySelector('.arrow-path-2')
    gsap.set([p1, p2], { strokeDasharray: 80, strokeDashoffset: 80 })
    const tl = gsap.timeline({ delay: 0.5 })
    tl.to([p1, p2], {
        strokeDashoffset: 0,
        duration: 0.8,
        stagger: 0.2,
        ease: 'power2.out',
    })
        .to(
            arrowContainer,
            { scale: 1.1, duration: 0.7, ease: 'expo.inOut' },
            '+=0.1',
        )
        .to(
            p1,
            {
                attr: { d: 'M 10 12.5 L 20 22.5 L 30 12.5' },
                duration: 0.7,
                ease: 'expo.inOut',
            },
            '<',
        )
        .to(
            p2,
            {
                attr: { d: 'M 30 20 L 20 30 L 10 20' },
                duration: 0.7,
                ease: 'expo.inOut',
            },
            '<',
        )
        .to(arrowContainer, {
            opacity: 0.2,
            duration: 1.5,
            repeat: -1,
            yoyo: true,
            ease: 'power1.inOut',
        })
}

const styleAxeptio = () => {
    console.log('GO')
    const updateAxeptio = () => {
        // 1. Trouver le "Shadow Host" (le parent qui contient le shadow-root)
        // Axeptio utilise souvent un ID commençant par 'axeptio_overlay'
        const host = document.querySelector('#axeptio_overlay .needsclick')

        if (host && host.shadowRoot) {
            // 2. Chercher le bouton à l'intérieur de l'ombre
            const btn = host.shadowRoot.querySelector('#axeptio_main_button')
            const sheet = new CSSStyleSheet()
            // On écrit le CSS normalement ici
            sheet.replaceSync(`
                #axeptio_main_button { 
                    background-color: #CBB69B !important; 
                }
                svg path { 
                    fill: #000 !important; 
                }
                svg path:first-child {
                    fill: transparent !important;
                }
            `)

            // On injecte la feuille de style dans le Shadow DOM
            host.shadowRoot.adoptedStyleSheets = [
                ...host.shadowRoot.adoptedStyleSheets,
                sheet,
            ]
        }
        return false
    }

    // On réessaie régulièrement pendant 5 secondes (le temps que le widget charge)
    let attempts = 0
    const interval = setInterval(() => {
        if (updateAxeptio() || attempts > 50) {
            clearInterval(interval)
        }
        attempts++
    }, 100)
}

/**
 * Initialization logic
 */
preloadAssets().then(() => {
    const loader = document.querySelector('.loader')
    if (loader) {
        gsap.to(loader, {
            opacity: 0,
            duration: 1.2,
            ease: 'power2.inOut',
            onComplete: () => {
                loader.remove()
                document.body.classList.remove('loading')
            },
        })
    }

    // Smooth scroll setup - increased lerp for more direct feel
    const lenis = new Lenis({
        lerp: 0.12,
        wheelMultiplier: 1.0,
        flat: true, // Avoid extra smoothing if available
    })
    lenis.on('scroll', ScrollTrigger.update)
    gsap.ticker.add((time) => lenis.raf(time * 1000))
    gsap.ticker.lagSmoothing(0)

    // Components
    initArrowAnimation()
    new StickyGridScroll()
})

styleAxeptio()
